import React from 'react';
import { VerticallyCenteredText } from 'src/shared/design-system/VerticallyCenteredText';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Border, Colors, Font, getSpacingMultiple, Spacing } from 'web-common/src/shared/styles';

export enum DishAvailabilityStatus {
  AVAILABLE,
  SOLD_OUT,
  UNAVAILABLE,
}

export interface IPillStyleProps {
  background?: string;
  color?: string;
  border?: string;
  fontSize?: string;
  width?: string;
  height?: string;
}

export const pillStyle = ({
  background = Colors.RADISH,
  color = Colors.WHITE,
  border = 'none',
  fontSize = Font.Size.S,
  width = getSpacingMultiple(10.5),
  height = Spacing.DOUBLE_HALF,
}: IPillStyleProps) => css`
  background: ${background};
  border: ${border};
  border-radius: calc(${height} / 2);
  color: ${color};

  font-family: ${Font.Family.SANS};
  font-size: ${fontSize};
  font-weight: ${Font.Weight.SEMI_BOLD};

  height: ${height};
  width: ${width};
`;

interface IPillButtonProps extends IPillStyleProps {
  disabled?: boolean;
}

const PillButton = styled.button<IPillButtonProps>`
  ${pillStyle}
  text-align: center;
  cursor: ${ifProp('disabled', 'unset', 'cursor')};
`;

interface IAddToCartButtonProps {
  availability: DishAvailabilityStatus;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  priceText?: string;
  width?: string;
  fontSize?: string;
}

export const AddToCartButton: React.FC<IAddToCartButtonProps> = ({
  availability,
  onClick,
  priceText,
  width,
  fontSize,
}) => {
  const matchResults = match({ availability, priceText })
    .with({ availability: DishAvailabilityStatus.SOLD_OUT }, () => ({
      disabled: true,
      text: 'Sold Out',
      border: `${Border.Base} ${Colors.GRAY}`,
    }))
    .with({ availability: DishAvailabilityStatus.UNAVAILABLE }, () => ({
      disabled: true,
      text: 'Unavailable',
      border: `${Border.Base} ${Colors.GRAY}`,
    }))
    .with({ priceText: undefined }, () => ({
      onClick,
      text: 'Add to Cart',
      border: `${Border.Base} ${Colors.RADISH}`,
      'data-cy': 'food-item-add-to-cart-button',
    }))
    .otherwise(() => ({
      onClick,
      text: `Add to Cart - ${priceText}`,
      border: `${Border.Base} ${Colors.RADISH}`,
      'data-cy': 'food-item-add-to-cart-button',
    }));

  const { text, ...pillButtonProps } = matchResults;
  return (
    <PillButton background='none' color={Colors.GRAY_DARK_30} width={width} fontSize={fontSize} {...pillButtonProps}>
      <VerticallyCenteredText>{text}</VerticallyCenteredText>
    </PillButton>
  );
};
